/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var core_1 = require("@angular/core");
var util_1 = require("./util");
/**
 * @hidden
 */
var DataService = /** @class */ (function () {
    function DataService() {
        this.grouped = false;
        this.groupIndices = [];
    }
    Object.defineProperty(DataService.prototype, "data", {
        get: function () {
            if (this.grouped) {
                return this._flatData;
            }
            return this._data;
        },
        set: function (data) {
            this._data = data;
            this.grouped = this.isGrouped(data);
            if (this.grouped) {
                this.groupIndices = this.getGroupIndices(data);
                this._flatData = this.flatten(data);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DataService.prototype, "itemsCount", {
        /**
         * @hidden
         * Used to get the actual items count, i.e. excluding the header items in case of grouping.
         */
        get: function () {
            if (!util_1.isPresent(this.data) || this.data.length === 0) {
                return 0;
            }
            var items = this.grouped ? this._flatData.filter(function (item) { return !item.header; }) : this.data;
            return items.length;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     * Used to determine if the component received grouped data.
     */
    DataService.prototype.isGrouped = function (data) {
        // GroupResult { aggregates: AggregateResult, field: string, items: object[], value: any }
        // https://www.telerik.com/kendo-angular-ui/components/dataquery/api/GroupResult/
        return (util_1.isPresent(data) && data.length !== 0) && util_1.isPresent(data[0]) && util_1.hasProps(data[0], ['aggregates', 'field', 'items', 'value']);
    };
    /**
     * @hidden
     * Used to calculate the last item index of each group.
     */
    DataService.prototype.getGroupIndices = function (data) {
        var groupIndices = [];
        for (var i = 0; i <= data.length - 1; i++) {
            groupIndices[i] = (groupIndices[i - 1] || 0) + data[i].items.length;
        }
        return groupIndices;
    };
    /**
     * @hidden
     * Used to get a flat array containing all items matching certain criteria.
     */
    DataService.prototype.filter = function (predicate) {
        var result = [];
        if (this.isGrouped(this.data)) {
            for (var i = 0; i <= this.groupIndices.length - 1; i++) {
                var matches = this.data[i].items.filter(predicate);
                if (matches) {
                    result = result.concat(matches);
                }
            }
        }
        else {
            result = this.data.filter(predicate);
        }
        return result;
    };
    /**
     * @hidden
     * Used to get the index of a given data item.
     */
    DataService.prototype.indexOf = function (item, startFrom) {
        if (startFrom === void 0) { startFrom = 0; }
        var predicate = function (element) {
            return element === item;
        };
        if (this.grouped) {
            predicate = function (element) {
                return element.value === item;
            };
        }
        return this.findIndex(predicate, startFrom);
    };
    /**
     * @hidden
     * Used to get the index of a data item based on an expression.
     */
    DataService.prototype.findIndex = function (predicate, startFrom) {
        if (startFrom === void 0) { startFrom = 0; }
        var index = -1;
        if (this.grouped) {
            var data = this._flatData.filter(function (item) { return !item.header && item.offsetIndex >= startFrom; });
            index = data.findIndex(predicate);
            index = data[index] ? data[index].offsetIndex : -1;
        }
        else {
            var data = this.data.slice(startFrom);
            var itemIndex = data.findIndex(predicate);
            index = itemIndex !== -1 ? itemIndex + startFrom : -1;
        }
        return index;
    };
    /**
     * @hidden
     * Used to get the closest group header prior to an item index.
     */
    DataService.prototype.closestGroup = function (index) {
        for (var i = index; i >= 0; i--) {
            if (this._flatData[i].header) {
                return this._flatData[i];
            }
        }
    };
    /**
     * @hidden
     * Used to get the first item matching the criteria.
     */
    DataService.prototype.find = function (predicate) {
        var index = this.findIndex(predicate);
        return this.itemAt(index);
    };
    /**
     * @hidden
     * Used to get the true index in a flattened data array.
     */
    DataService.prototype.flatIndex = function (index) {
        if (this.itemsCount === 0) {
            return -1;
        }
        if (this.grouped) {
            var match = this._flatData.find(function (item) { return !item.header && item.offsetIndex === index; });
            if (match) {
                return match.index;
            }
        }
        else {
            return index;
        }
        return -1;
    };
    /**
     * @hidden
     * Used to get the item at the provided index.
     */
    DataService.prototype.itemAt = function (index) {
        var dataItem;
        if (this.itemsCount === 0) {
            return dataItem;
        }
        if (this.grouped) {
            var match = this._flatData.find(function (item) { return !item.header && item.offsetIndex === index; });
            if (match) {
                dataItem = match.value;
            }
        }
        else {
            dataItem = this.data[index];
        }
        return dataItem;
    };
    /**
     * @hidden
     * Used to get the group at the provided index.
     */
    DataService.prototype.groupAt = function (index) {
        if (this.itemsCount === 0 || !this.isGrouped) {
            return;
        }
        return this._flatData.find(function (item) { return item.header && item.index === index; });
    };
    /**
     * @hidden
     * Used to get the field by which the data is grouped.
     */
    DataService.prototype.groupField = function () {
        if (this.itemsCount === 0 || !this.isGrouped) {
            return null;
        }
        return this._data[0].field;
    };
    /**
     * @hidden
     * Used to get the group to which a dataItem belongs.
     */
    DataService.prototype.itemGroup = function (item) {
        if (!item || this.itemsCount === 0 || !this.isGrouped) {
            return;
        }
        var fieldName = this.groupField();
        if (fieldName) {
            return util_1.getter(item, fieldName);
        }
    };
    DataService.prototype.flatten = function (data, group, offset, groupIndex) {
        if (group === void 0) { group = undefined; }
        if (offset === void 0) { offset = 0; }
        if (groupIndex === void 0) { groupIndex = 0; }
        var flat = [];
        if (util_1.isPresent(group)) {
            flat.push({
                header: true,
                index: groupIndex + offset,
                offsetIndex: groupIndex,
                value: group
            });
        }
        for (var i = 0; i < data.length; i++) {
            var result = [];
            if (data[i].items) {
                result = this.flatten(data[i].items, data[i].value, offset, i);
                offset = offset + data[i].items.length;
            }
            else {
                result.push({
                    header: false,
                    index: groupIndex + offset + i + 1,
                    offsetIndex: offset + i,
                    value: data[i]
                });
            }
            flat = flat.concat(result);
        }
        return flat;
    };
    DataService = tslib_1.__decorate([
        core_1.Injectable()
    ], DataService);
    return DataService;
}());
exports.DataService = DataService;
